import { api } from "@/api/client";

export interface AdminProject {
  id: string;
  name: string;
  plan_id: string;
  suspended: boolean;
  suspended_reason: string | null;
  paid_until: string | null;
  created_at: string;
  // Snapshotted plan terms — only the project-detail endpoint populates them.
  // Stored on the project row at buy/upgrade time and can drift from plans.*
  // if the operator edits the plan after a customer signed up.
  cycle_days?: number;
  monthly_credits?: string;
  rps_limit?: number;
  sse_connections_max?: number;
  max_tokens_per_sse_conn?: number;
  // overage_credit_price_usd encoding:
  //   "-1" → no cap (unlimited overage)
  //    "0" → hard cap (block on quota exhaustion)
  //   ">0" → soft cap, $X per credit billed for overage
  overage_credit_price_usd?: string;
  monthly_price_usd?: string;
  member_count: number;
  owner_email: string | null;
}

export interface AdminUserProject {
  id: string;
  name: string;
  plan_id: string;
  paid_until: string | null;
  suspended: boolean;
  role: "owner" | "developer" | "billing";
  joined_at: string;
}

export interface AdminPayment {
  id: string;
  project_id: string;
  project_name: string | null;
  amount_usdc: number;
  tx_signature: string | null;
  from_wallet: string | null;
  received_at: string;
  status: string;
  plan_id: string | null;
  extends_to: string | null;
  note: string | null;
}

export interface AdminUser {
  id: string;
  email: string;
  role: "user" | "admin";
  created_at: string;
  last_login_at: string | null;
  owner_projects_count: number;
  member_projects_count: number;
}

export interface AdminPlan {
  id: string;
  display_name: string;
  monthly_price_usd: string;
  monthly_credits: string;
  cycle_days: number;
  rps_limit: number;
  overage_credit_price_usd: string;
  sse_connections_max: number;
  max_tokens_per_sse_conn: number;
  hidden: boolean;
  sort_order: number;
}

export interface AdminSweep {
  id: string;
  payment_id: string;
  project_id: string;
  project_name: string | null;
  autoincrement_id: number;
  public_key: string;
  status: string;
  expires_at: string;
  received_amount: string | null;
  received_at: string | null;
  received_tx_sig: string | null;
  swept_at: string | null;
  swept_tx_sig: string | null;
  created_at: string;
}

export interface AdminAuditRow {
  id: number;
  actor_user_id: string | null;
  action: string;
  target_type: string;
  target_id: string;
  details: unknown;
  created_at: string;
}

export interface AdminProjectMember {
  user_id: string;
  email: string;
  role: string;
  user_created_at: string;
  last_login_at: string | null;
}

export interface AdminProjectKey {
  id: string;
  name: string;
  created_at: string;
  revoked_at: string | null;
}

export interface AdminUsageBucketRow {
  bucket: string;
  endpoint: string;
  calls: number;
  credits: number;
  avg_latency_ms: number;
}

export interface AdminUsageResponse {
  granularity: string;
  total_credits: number;
  rows: AdminUsageBucketRow[];
}

export interface AdminIpRow {
  ip: string;
  country: string;
  calls: number;
  credits: number;
  first_seen: string;
  last_seen: string;
}

export interface AdminPromo {
  id: string;
  code: string;
  kind: "percent" | "fixed_usd";
  value: string;
  scope: "once" | "lifetime";
  new_project_only: boolean;
  plan_allowlist: string[] | null;
  per_user_limit: number;
  max_enters: number | null;
  enter_count: number;
  max_redemptions: number | null;
  redeem_count: number;
  valid_from: string | null;
  valid_until: string | null;
  active: boolean;
  created_at: string;
}

export interface AdminPromoUsage {
  redemption_id: string;
  project_id: string | null;
  project_name: string | null;
  user_email: string | null;
  status: string;
  discount_usd: string;
  entered_at: string;
  redeemed_at: string | null;
}

export interface AdminPromoDetail {
  promo: AdminPromo;
  total_discount_usd: string;
  usage: AdminPromoUsage[];
}

export interface CreatePromoInput {
  code: string;
  kind: "percent" | "fixed_usd";
  value: string;
  scope: "once" | "lifetime";
  new_project_only?: boolean;
  plan_allowlist?: string[] | null;
  per_user_limit?: number;
  max_enters?: number | null;
  max_redemptions?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  active?: boolean;
}

export type UpdatePromoInput = Partial<Omit<CreatePromoInput, "code">>;

export const adminApi = {
  // projects
  listProjects: (q: { q?: string; plan?: string; suspended?: boolean; limit?: number } = {}) =>
    api.get<{ projects: AdminProject[] }>("/admin/projects", { query: q }),
  getProject: (pid: string) => api.get<AdminProject>(`/admin/projects/${pid}`),
  suspend: (pid: string, reason?: string) => api.post<{ ok: true }>(`/admin/projects/${pid}/suspend`, { reason }),
  unsuspend: (pid: string) => api.post<{ ok: true }>(`/admin/projects/${pid}/unsuspend`),
  setPlan: (pid: string, plan_id: string) => api.patch<{ ok: true }>(`/admin/projects/${pid}/plan`, { plan_id }),
  setPaidUntil: (pid: string, paid_until: string) => api.patch<{ ok: true }>(`/admin/projects/${pid}/paid-until`, { paid_until }),
  // Per-project plan snapshot override. Every field optional; only present
  // fields are written. monthly_credits/_price/overage are decimal strings.
  editPlanConfig: (pid: string, patch: {
    monthly_credits?: string; cycle_days?: number; rps_limit?: number;
    sse_connections_max?: number; max_tokens_per_sse_conn?: number;
    monthly_price_usd?: string; overage_credit_price_usd?: string;
  }) => api.patch<{ ok: true }>(`/admin/projects/${pid}/plan-config`, patch),
  transferOwner: (pid: string, email: string) => api.post<{ ok: true }>(`/admin/projects/${pid}/transfer-owner`, { email }),

  // project detail tabs
  projectMembers: (pid: string) =>
    api.get<{ members: AdminProjectMember[] }>(`/admin/projects/${pid}/members`),
  projectKeys: (pid: string) =>
    api.get<{ keys: AdminProjectKey[] }>(`/admin/projects/${pid}/keys`),
  projectPayments: (pid: string, q: { status?: string; limit?: number } = {}) =>
    api.get<{ payments: AdminPayment[] }>(`/admin/projects/${pid}/payments`, { query: q }),
  projectUsage: (
    pid: string,
    q: { from?: string; to?: string; api_key_id?: string; granularity?: "day" | "hour" } = {},
  ) => api.get<AdminUsageResponse>(`/admin/projects/${pid}/usage`, { query: q }),
  projectIps: (pid: string, q: { from?: string; to?: string; search?: string; limit?: number } = {}) =>
    api.get<{ ips: AdminIpRow[] }>(`/admin/projects/${pid}/ips`, { query: q }),

  // payments
  recordPayment: (pid: string, body: {
    amount_usdc: number; tx_signature?: string | null; from_wallet?: string | null;
    plan_id?: string | null; note?: string | null;
  }) => api.post<AdminPayment>(`/admin/projects/${pid}/payments`, body),
  refundPayment: (id: string, reason?: string) =>
    api.post<{ ok: true; paid_until_reverted: boolean; paid_until: string | null }>(`/admin/payments/${id}/refund`, { reason }),
  confirmPayment: (id: string) => api.post<{ ok: true }>(`/admin/payments/${id}/confirm`),
  rejectPayment: (id: string, reason?: string) => api.post<{ ok: true }>(`/admin/payments/${id}/reject`, { reason }),
  listPayments: (q: { status?: string; limit?: number } = {}) =>
    api.get<{ payments: AdminPayment[] }>("/admin/payments", { query: q }),

  // onchain sweeps
  listSweeps: (q: { status?: string; project_id?: string; limit?: number } = {}) =>
    api.get<{ sweeps: AdminSweep[] }>("/admin/sweeps", { query: q }),

  // plans
  listAllPlans: () => api.get<{ plans: AdminPlan[] }>("/admin/plans"),
  createPlan: (plan: AdminPlan) => api.post<{ ok: true }>("/admin/plans", plan),
  // `id` may be supplied to rename the plan; cascades to projects.plan_id +
  // payments.plan_id via the ON UPDATE CASCADE FKs.
  updatePlan: (id: string, patch: Partial<Pick<AdminPlan,
    "id" | "display_name" | "monthly_price_usd" | "monthly_credits" | "cycle_days" |
    "rps_limit" | "overage_credit_price_usd" | "sse_connections_max" |
    "max_tokens_per_sse_conn" | "hidden" | "sort_order"
  >>) => api.patch<{ ok: true }>(`/admin/plans/${id}`, patch),

  // promo codes
  listPromos: () => api.get<{ promos: AdminPromo[] }>("/admin/promos"),
  getPromo: (id: string) => api.get<AdminPromoDetail>(`/admin/promos/${id}`),
  createPromo: (body: CreatePromoInput) => api.post<AdminPromo>("/admin/promos", body),
  updatePromo: (id: string, patch: UpdatePromoInput) =>
    api.patch<AdminPromo>(`/admin/promos/${id}`, patch),

  // keys / sessions
  revokeKey: (kid: string) => api.delete<{ ok: true }>(`/admin/keys/${kid}`),
  revokeSession: (sid: string) => api.delete<{ ok: true }>(`/admin/sessions/${sid}`),

  // users
  listUsers: (q: { q?: string; role?: string; limit?: number } = {}) =>
    api.get<{ users: AdminUser[] }>("/admin/users", { query: q }),
  setUserRole: (uid: string, role: "user" | "admin") =>
    api.patch<{ ok: true }>(`/admin/users/${uid}/role`, { role }),
  listUserProjects: (uid: string) =>
    api.get<{ projects: AdminUserProject[] }>(`/admin/users/${uid}/projects`),

  // audit
  audit: (q: { limit?: number; target_type?: string } = {}) =>
    api.get<{ rows: AdminAuditRow[] }>("/admin/audit", { query: q }),

  // ip denylist
  listBlockedIps: () => api.get<{ blocks: AdminIpBlock[] }>("/admin/ip-blocks"),
  blockIp: (ip: string, body: { reason?: string; ttl_secs?: number | null }) =>
    api.post<{ ok: true }>("/admin/ip-blocks", { ip, ...body }),
  unblockIp: (ip: string) => api.delete<{ ok: true }>(`/admin/ip-blocks/${encodeURIComponent(ip)}`),
};

export interface AdminIpBlock {
  ip: string;
  reason: string | null;
  blocked_at: string | null;
  ttl_secs: number;     // -1 = no expiry
}
